import { useCallback, useEffect, useState } from "react";
import { dashFetch } from "@/api/client";

export default function MfaPanel() {
  const [enabled, setEnabled] = useState(false);
  const [secret, setSecret] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await dashFetch<{ ok?: boolean; totpEnabled?: boolean }>("/dashboard/api/account/mfa", { cache: "no-store" });
      setEnabled(Boolean(r?.totpEnabled));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const begin = async () => {
    setBusy(true);
    setMsg("");
    try {
      const r = await dashFetch<{ ok?: boolean; secret?: string; otpauthUrl?: string; error?: string }>(
        "/dashboard/api/account/mfa",
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "begin" }) }
      );
      if (!r?.ok) throw new Error(r?.error || "Could not start enrollment");
      setSecret(String(r.secret || ""));
      setOtpauthUrl(String(r.otpauthUrl || ""));
    } catch (e) {
      setMsg(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setMsg("");
    try {
      const r = await dashFetch<{ ok?: boolean; totpEnabled?: boolean; error?: string; message?: string }>(
        "/dashboard/api/account/mfa",
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "confirm", code }) }
      );
      if (!r?.ok) throw new Error(r?.message || r?.error || "Invalid code");
      setEnabled(true);
      setSecret("");
      setOtpauthUrl("");
      setCode("");
      setMsg("Authenticator enrolled.");
    } catch (e) {
      setMsg(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMsg("");
    try {
      const r = await dashFetch<{ ok?: boolean; error?: string; message?: string }>(
        "/dashboard/api/account/mfa",
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "disable", code }) }
      );
      if (!r?.ok) throw new Error(r?.message || r?.error || "Enter a current authenticator code");
      setEnabled(false);
      setCode("");
      setMsg("Authenticator removed.");
    } catch (e) {
      setMsg(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mfaPanel">
      <p className="muted profileLead">
        Magic-link email is the first factor. Authenticator TOTP is required for SOC 2 operator MFA.
      </p>
      <div className="mfaStatusRow">
        <span className={"mfaStatus" + (enabled ? " on" : " off")}>{enabled ? "TOTP on" : "TOTP off"}</span>
        <span className="muted">{enabled ? "This account requires an authenticator code at sign-in." : "Enroll an authenticator app to protect this account."}</span>
      </div>
      {!enabled && !secret ? (
        <button type="button" className="btn primary" disabled={busy} onClick={() => void begin()}>
          Enroll authenticator
        </button>
      ) : null}
      {secret ? (
        <div className="mfaEnroll">
          <p className="muted" style={{ marginTop: 0 }}>
            Add this key in your authenticator app, then confirm a 6-digit code.
          </p>
          <code className="mfaSecret">{secret}</code>
          {otpauthUrl ? (
            <a className="btn" href={otpauthUrl}>Open in authenticator</a>
          ) : null}
          <div className="profileFormRow">
            <input
              className="settingsInput"
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <button type="button" className="btn primary" disabled={busy || code.length !== 6} onClick={() => void confirm()}>
              Confirm
            </button>
          </div>
        </div>
      ) : null}
      {enabled ? (
        <div className="mfaDisable">
          <p className="muted" style={{ marginBottom: 8 }}>Enter a current authenticator code to turn MFA off.</p>
          <div className="profileFormRow">
            <input
              className="settingsInput"
              placeholder="Code to disable"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <button type="button" className="btn danger" disabled={busy || code.length !== 6} onClick={() => void disable()}>
              Disable MFA
            </button>
          </div>
        </div>
      ) : null}
      {msg ? <p className="muted" style={{ marginTop: 10 }}>{msg}</p> : null}
    </div>
  );
}
